/**
 * School app field drafts (release lines U6, R10, R13, KTD4).
 *
 * A school edits one working copy of its app. Fields the app reads at runtime
 * go live when saved; everything else waits for staff to release it. This file
 * names every field a school may edit and says which of them are stored as a
 * VALUE draft in `MobileAppFieldDraft`.
 *
 * Only the build-kind fields carry a value: the app name and the three baked
 * images. Their live homes (`MobileApp.name` and the build asset slots) are
 * what the build snapshot and the public asset manifest read, so the draft has
 * to live somewhere those readers never look. That is the whole guarantee for
 * R13: nothing device-facing reads the draft table, and a draft reaches its
 * live home only through a staff release.
 *
 * Every other field keeps its value where it lives today (the store listing
 * row, the onboarding config, a runtime slot). A store-class or OTA-class save
 * also writes a STATUS-ONLY draft row, with a null value, so the saved and
 * release-requested dates have somewhere to live (R15).
 *
 * Keys reuse the asset slot names wherever the field IS a slot, so a draft key
 * and the slot it applies to are one string. The other keys are kebab-case in
 * the same style. U7 adds the per-line delivery catalog over these keys.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-23-2135-feat-app-release-lines-and-school-drafts-plan.md (U6)
 */

import { z } from 'zod'
import { AssetCommitRequestSchema } from './mobile-app-asset'

/**
 * Every field a school may edit in its app's working copy (R10).
 *
 *  - `app-name`: the on-device label under the icon (`MobileApp.name`, the
 *    build config's `appName`). Distinct from `store-title`, the listing name.
 *  - `store-*`: the listing row's columns (`MobileAppStoreListing`).
 *  - `welcome-text` / `login-text`: the pre-login copy on
 *    `MobileApp.onboardingConfig` (the carousel slides, and the login screen).
 *  - the rest: asset slot names, verbatim (`MOBILE_APP_ASSET_SLOTS`).
 */
export const SCHOOL_APP_FIELD_KEYS = [
  'app-name',
  'store-title',
  'store-subtitle',
  'store-description',
  'store-keywords',
  'store-promotional-text',
  'store-marketing-url',
  'store-support-url',
  'store-privacy-policy-url',
  'welcome-text',
  'login-text',
  'onboard-1',
  'onboard-2',
  'onboard-3',
  'login-bg',
  'logo-small-light',
  'logo-small-dark',
  'main-logo-light',
  'main-logo-dark',
  'app-icon',
  'splash',
  'adaptive-icon-foreground'
] as const
export const SchoolAppFieldKeySchema = z.enum(SCHOOL_APP_FIELD_KEYS)
export type SchoolAppFieldKey = z.infer<typeof SchoolAppFieldKeySchema>

/**
 * The fields whose draft carries a VALUE (KTD4). Each is baked into the binary,
 * so its live home is read by the build, and the draft has to be kept apart
 * from it until staff release it.
 *
 * NOT the school's live asset allowlist: `SCHOOL_EDITABLE_ASSET_SLOTS`, which
 * the live commit route enforces, stays limited to runtime slots. A school
 * reaches these fields only through the draft store.
 */
export const BUILD_DRAFT_FIELDS = ['app-name', 'app-icon', 'splash', 'adaptive-icon-foreground'] as const
export type BuildDraftField = (typeof BUILD_DRAFT_FIELDS)[number]

export function isBuildDraftField(value: string): value is BuildDraftField {
  return (BUILD_DRAFT_FIELDS as readonly string[]).includes(value)
}

/**
 * The longest app name a school may draft (R19). Apple and Play both cap the
 * name at 30 characters, and the on-device label is truncated long before
 * that, so the editor's counter and this rule share one number.
 */
export const SCHOOL_APP_NAME_MAX_LENGTH = 30
export const SCHOOL_APP_NAME_TOO_LONG_MESSAGE = `App name cannot exceed ${SCHOOL_APP_NAME_MAX_LENGTH} characters (App Store / Play limit)`

/**
 * An app-name draft, capped at {@link SCHOOL_APP_NAME_MAX_LENGTH}. Tighter than
 * the registry PATCH's 200-character bound on `MobileApp.name`, which staff
 * still write directly; the placeholder rule that PATCH also applies runs in
 * the backend, which validates with the PATCH schema itself. Save and apply run
 * this same schema, so a draft staff apply is one a school could have saved.
 */
export const AppNameDraftValueSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { message: 'App name is required' })
      .max(SCHOOL_APP_NAME_MAX_LENGTH, { message: SCHOOL_APP_NAME_TOO_LONG_MESSAGE })
  })
  .strict()
export type AppNameDraftValue = z.infer<typeof AppNameDraftValueSchema>

/**
 * An image draft: the committed Cloudinary upload, in the exact shape the slot
 * commit endpoint takes, so applying it is that commit.
 */
export const ImageDraftValueSchema = AssetCommitRequestSchema
export type ImageDraftValue = z.infer<typeof ImageDraftValueSchema>

/** The value schema for each build draft field. */
export const BUILD_DRAFT_VALUE_SCHEMAS = {
  'app-name': AppNameDraftValueSchema,
  'app-icon': ImageDraftValueSchema,
  splash: ImageDraftValueSchema,
  'adaptive-icon-foreground': ImageDraftValueSchema
} as const satisfies Record<BuildDraftField, z.ZodTypeAny>

export type BuildDraftValue = AppNameDraftValue | ImageDraftValue

/**
 * One draft row as a reader sees it. `value` is null on a status-only row
 * (store and OTA class). `appliedAt` is set once staff copied a build draft
 * into its live home; saving again clears it, which is a new pending draft.
 */
export const SchoolAppFieldDraftSchema = z.object({
  fieldKey: SchoolAppFieldKeySchema,
  value: z.union([AppNameDraftValueSchema, ImageDraftValueSchema]).nullable(),
  savedAt: z.string().datetime(),
  savedByUserId: z.string().nullable(),
  releaseRequestedAt: z.string().datetime().nullable(),
  appliedAt: z.string().datetime().nullable()
})
export type SchoolAppFieldDraft = z.infer<typeof SchoolAppFieldDraftSchema>
